import { Badge, type BadgeProps, Icon, cn } from '@sr/ui-library';
import { type AppBadgePreset } from './appBadgeConstants';
import { type AppBadgeType } from './appBadgeTypes';

export interface AppBadgeProps extends BadgeProps {
  /** Show `type.shortLabel` instead of the full label; falls back to `children` when unset. */
  shortLabel?: boolean;
  /** A preset from `SERVICE_BADGE_PRESETS` / `STATUS_BADGE_PRESETS` / `ATTRIBUTE_BADGE_PRESETS`. */
  type: AppBadgePreset;
}

/**
 * A single badge driven by presentation meta from `appBadgeConstants`. Service
 * pills pass `iconProps`; status pills pass `variant` + `hasIndicator` — both
 * map straight onto `Badge` without branching.
 *
 *   <AppBadge type={SERVICE_BADGE_PRESETS.collectionAccount} />
 *   <AppBadge type={STATUS_BADGE_PRESETS.Workspace.active} />
 *   <AppBadge type={SERVICE_BADGE_PRESETS.residuals} shortLabel />
 */
export const AppBadge = ({
  children,
  className,
  shortLabel,
  type,
  ...props
}: AppBadgeProps) => {
  // Every preset satisfies `AppBadgeType` by construction (see appBadgeTypes.ts),
  // so this widening is structurally safe — it just lets us destructure fields
  // that aren't common to every member of the `AppBadgePreset` union.
  const meta: AppBadgeType = type;
  const {
    children: label,
    className: typeClassName,
    hasIndicator,
    iconProps,
    shortLabel: typeShortLabel,
    variant,
  } = meta;

  return (
    <Badge
      className={cn(typeClassName, className)}
      hasIndicator={hasIndicator}
      icon={iconProps ? <Icon {...iconProps} /> : undefined}
      style="foundation"
      variant={variant}
      {...props}
    >
      {shortLabel ? (children ?? typeShortLabel ?? label) : (children ?? label)}
    </Badge>
  );
};
